import { AxiosPromise, AxiosResponse } from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import MojoApiFactory from '../config/api-factory';
import {
  ChatbotSendMessageRequest,
  ChatbotSendMessageResponse,
  ChatbotThreadListResponse,
  ChatbotThreadMessagesResponse,
  StreamEvent,
  ReferenceCardResponse,
  SuggestionsResponse,
} from '../models/chatbot/types';

export const getChatThreadMessages = async (threadId: string) => {
  return (await MojoApiFactory.chatbot.get(
    `chat_messages?threadId=${threadId}`
  )) as AxiosResponse<ChatbotThreadMessagesResponse>;
};

export const getChatThreadList = async (page = 1, perPage = 20) => {
  return (await MojoApiFactory.chatbot.get(
    `chat_threads?page=${page}&per_page=${perPage}`
  )) as AxiosResponse<ChatbotThreadListResponse>;
};

export const sendMessage = async ({
  threadId,
  message,
}: ChatbotSendMessageRequest) => {
  // Use new chat message batch endpoint that creates both user and assistant messages
  return MojoApiFactory.chatbot.post(`chat_messages/completions`, {
    threadId,
    content: message,
  }) as AxiosPromise<ChatbotSendMessageResponse>;
};

export const sendMessageStream = async ({
  threadId,
  message,
  onEvent,
  signal,
}: ChatbotSendMessageRequest & {
  onEvent: (event: StreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> => {
  fetchEventSource(
    `${MojoApiFactory.chatbot.defaults.baseURL}chat_messages/completions`,
    {
      openWhenHidden: true, // Keep SSE alive when tab is hidden to prevent reconnect/retry
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        // Include authentication headers from the axios instance
        ...(MojoApiFactory.chatbot.defaults.headers.common as Record<
          string,
          string
        >),
      },
      body: JSON.stringify({
        content: message,
        threadId,
      }),
      onmessage(event: any) {
        if (event.event === 'ping') return; // Skip empty ping events

        try {
          const data = JSON.parse(event.data);
          const eventType = event.event || 'message_update';
          onEvent({
            type: eventType as StreamEvent['type'],
            data,
          } as StreamEvent);
        } catch (e) {
          console.warn('Failed to parse SSE data:', event.data);
        }
      },
      onerror(error: any) {
        onEvent({ type: 'error', data: { error: error.message || error } });
        throw error; // Stop retrying
      },
    }
  );
};

export const getReferenceCard = async (
  referenceType: string,
  referenceId: string
) => {
  return (await MojoApiFactory.chatbot.get(
    `reference_cards/${referenceType}/${referenceId}?link_format=universal`
  )) as AxiosResponse<ReferenceCardResponse>;
};

export const getSuggestions = async () => {
  return (await MojoApiFactory.chatbot.get(
    'suggestions'
  )) as AxiosResponse<SuggestionsResponse>;
};
